/**
 * Shared platform-context columns.
 *
 * Every exception config renders the same FP-scoped header columns on
 * the platform-report variant. Define them once and spread into each
 * config's columns array. The browser view shows two cells:
 *
 *   Franchise (consolidated: name + country chip + fp_code + status)
 *   Data Age
 *
 * Excel exports still emit FP Code / Franchise / FP Status as three
 * separate columns via the backend `COLUMNS` arrays - this collapse is
 * a render-time convenience only.
 */
import React from 'react'
import type { ExceptionColumnDef } from '../types'
import { FranchiseCell, extractFranchiseFields } from './franchiseCell'
import { buildRowInspectorLink } from '../../../utils/urls'

/**
 * Per-row inspector deep link for the Franchise cell - delegates to the
 * single shared ``buildRowInspectorLink`` builder so the tenure id (row
 * ``fp_id``) is pinned and the exception-vs-dashboard logic lives in one
 * place.
 */
const resolveFranchiseDeepLink = (row: Record<string, any>): string | undefined =>
  buildRowInspectorLink(row)

export const PLATFORM_CONTEXT_COLUMNS: ExceptionColumnDef[] = [
  {
    key: 'fp_name',
    label: 'Franchise',
    align: 'left',
    minWidth: 160,
    contexts: ['platform'],
    render: (_value, row) => {
      const { fpCode, fpName, fpStatus } = extractFranchiseFields(row)
      return React.createElement(FranchiseCell, {
        fpCode,
        fpName,
        fpStatus,
        to: resolveFranchiseDeepLink(row),
      })
    },
    // Sort by franchise name; fall back to fp_code so unnamed rows still
    // group by FP.
    getSortValue: row => (row.fp_name ?? row.franchise_name ?? row.fp_code ?? '').toString(),
    exportFormat: 'text',
  },
  {
    key: 'last_sync_at',
    label: 'Data Age',
    type: 'sync_status',
    contexts: ['platform'],
  },
]
